import React, { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { apiRequest, API_BASE_URL } from "../api/client";

function About({ onOpenAuth, onOpenMenu }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [about, setAbout] = useState(null);

  const fetchAbout = async () => {
    setLoading(true);
    setError("");
    const res = await apiRequest("api/page_sections?page_module=about_us", { method: "GET" });
    if (!res.ok) {
      setError(res.data?.message || "Failed to load About Us content.");
      setAbout(null);
    } else {
      setAbout(res.data || null);
    }
    setLoading(false);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      const res = await apiRequest("api/page_sections?page_module=about_us", { method: "GET" });
      if (!alive) return;
      if (!res.ok) {
        setError(res.data?.message || "Failed to load About Us content.");
        setAbout(null);
      } else {
        setAbout(res.data || null);
      }
      setLoading(false);
    })().catch(() => {
      if (!alive) return;
      setError("Failed to load About Us content.");
      setAbout(null);
      setLoading(false);
    });

    return () => {
      alive = false;
    };
  }, []);

  const heroSection = useMemo(() => {
    const first = Array.isArray(about?.page_sections) ? about.page_sections[0] : null;
    return first && typeof first === "object" ? first : null;
  }, [about?.page_sections]);

  const grouped = useMemo(() => {
    return Array.isArray(about?.grouped_sections) ? about.grouped_sections : [];
  }, [about?.grouped_sections]);

  const itemsByType = useMemo(() => {
    const map = new Map();
    grouped.forEach((g) => {
      const key = String(g?.section_type || "").trim();
      const items = Array.isArray(g?.items) ? g.items : [];
      if (key) map.set(key, items);
    });
    return map;
  }, [grouped]);

  const heroImageUrl = useMemo(() => {
    const raw = heroSection?.image_url;
    if (!raw) return "";
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${API_BASE_URL.replace(/\/$/, "")}${raw.startsWith("/") ? "" : "/"}${raw}`;
  }, [heroSection?.image_url]);

  const resolveImageUrl = (raw) => {
    const value = typeof raw === "string" ? raw.trim() : "";
    if (!value) return "";
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    return `${API_BASE_URL.replace(/\/$/, "")}${value.startsWith("/") ? "" : "/"}${value}`;
  };

  const title = heroSection?.title?.trim?.() ? heroSection.title : "About Us";
  const subTitle = heroSection?.sub_title?.trim?.() ? heroSection.sub_title : "";
  const heroDescription = heroSection?.description?.trim?.() ? heroSection.description : "";

  const featuresData = itemsByType.get("features_data") || [];
  const whatWeDoData = itemsByType.get("what_we_do_data") || [];
  const stepsData = itemsByType.get("steps_data") || [];
  const privacyPolicyData = itemsByType.get("privacy_policy_data") || [];

  const featureByTitle = useMemo(() => {
    const m = new Map();
    featuresData.forEach((item) => {
      const key = String(item?.title || "").trim().toLowerCase();
      if (key) m.set(key, item);
    });
    return m;
  }, [featuresData]);

  const whoWeAre = featureByTitle.get("who we are") || null;
  const ourMission = featureByTitle.get("our mission") || null;
  const builtOnTrust = featureByTitle.get("built on trust") || null;
  const ourPromise = featureByTitle.get("our promise") || null;

  const privacyPromise = useMemo(() => {
    const first = Array.isArray(privacyPolicyData) ? privacyPolicyData[0] : null;
    return first && typeof first === "object" ? first : null;
  }, [privacyPolicyData]);

  const renderText = (text) => {
    const value = typeof text === "string" ? text : "";
    return value.replace(/\r\n/g, "\n").trim();
  };

  const whatWeDoIcon = (key) => {
    const k = String(key || "").trim().toLowerCase();
    const common = "h-8 w-8";
    if (k.includes("compare")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M7 7h10M7 12h6M7 17h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6Z" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    }
    if (k.includes("save")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 2l2.1 6.4H21l-5.6 4.1L17.5 19 12 14.9 6.5 19l2.1-6.5L3 8.4h6.9L12 2Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (k.includes("simplify")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M7 7h10M7 12h10M7 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M4 4h16v16H4V4Z" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    }
    return (
      <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3l7 4v6c0 5-3 8-7 8s-7-3-7-8V7l7-4Z" stroke="currentColor" strokeWidth="2" />
        <path d="m9 12 2 2 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const stepIcon = (titleText) => {
    const k = String(titleText || "").toLowerCase();
    const common = "h-8 w-8";
    if (k.includes("privacy")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M12 3l7 4v6c0 5-3 8-7 8s-7-3-7-8V7l7-4Z" stroke="currentColor" strokeWidth="2" />
          <path d="M9.5 11.5a2.5 2.5 0 0 1 5 0V14H9.5v-2.5Z" stroke="currentColor" strokeWidth="2" />
          <path d="M9 14h6v4H9v-4Z" stroke="currentColor" strokeWidth="2" />
        </svg>
      );
    }
    return (
      <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m9 12 2 2 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  };

  const featureIcon = (titleText) => {
    const k = String(titleText || "").trim().toLowerCase();
    const common = "h-10 w-10";
    if (k.includes("mission")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10Z"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path
            d="m9 12 2 2 4-5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    if (k.includes("trust")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12 3l7 4v6c0 5-3 8-7 8s-7-3-7-8V7l7-4Z"
            stroke="currentColor"
            strokeWidth="2"
          />
          <path d="m9 12 2 2 4-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (k.includes("promise")) {
      return (
        <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M12.1 20.3 4.6 13a5 5 0 0 1 0-7.1 5 5 0 0 1 7.1 0l.3.3.3-.3a5 5 0 0 1 7.1 0 5 5 0 0 1 0 7.1l-7.5 7.3a1 1 0 0 1-1.4 0Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    }
    return (
      <svg className={common} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
          stroke="currentColor"
          strokeWidth="2"
        />
        <path
          d="M4 20a8 8 0 0 1 16 0"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A] flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 md:px-6 md:py-8">
        {loading ? (
          <section className="rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm md:p-10">
            <p className="text-[16px] text-[#6E7B86]">Loading...</p>
          </section>
        ) : error ? (
          <section className="rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm md:p-10">
            <p className="text-[16px] leading-relaxed text-red-600">{error}</p>
            <button
              type="button"
              onClick={fetchAbout}
              className="mt-3 rounded-md bg-[#7352C7] px-4 py-2 text-[16px] font-semibold text-white hover:bg-[#5f43a8]"
            >
              Retry
            </button>
          </section>
        ) : (
          <div className="space-y-6">
            {/* Hero */}
            <section className="overflow-hidden rounded-3xl border border-[#E6EAF0] bg-white shadow-sm">
              <div className="grid gap-6 p-6 md:grid-cols-2 md:gap-10 md:p-10">
                <div className="flex flex-col justify-center">
                  <h1 className="text-[28px] font-semibold leading-tight tracking-tight text-[#24303A]">
                    {title}
                  </h1>
                  {subTitle ? (
                    <p className="mt-3 text-[16px] leading-relaxed text-[#6E7B86]">
                      {renderText(subTitle)}
                    </p>
                  ) : null}
                  {heroDescription ? (
                    <p className="mt-3 whitespace-pre-line text-[16px] leading-relaxed text-[#6E7B86]">
                      {renderText(heroDescription)}
                    </p>
                  ) : null}
                  <div className="mt-5 flex flex-wrap gap-3">
                    <a
                      href="#"
                      className="inline-flex items-center justify-center rounded-full bg-[#7352C7] px-5 py-2 text-[16px] font-semibold text-white"
                    >
                      Get the App
                    </a>
                  </div>
                </div>
                <div className="relative">
                  {heroImageUrl ? (
                    <img
                      src={heroImageUrl}
                      alt={title}
                      className="h-[260px] w-full rounded-2xl object-cover md:h-full md:min-h-[320px]"
                    />
                  ) : (
                    <div className="h-[260px] w-full rounded-2xl border border-[#E6EAF0] bg-gradient-to-br from-[#F6F3FF] via-[#F5F7FB] to-[#FFF4EA] md:h-full md:min-h-[320px]" />
                  )}
                </div>
              </div>

              {/* Features */}
              <div className="border-t border-[#E6EAF0] bg-[#FBFCFF] p-6 md:p-8">
                <div className="grid gap-4 md:grid-cols-1">
                  {[...(featuresData || [])]
                    .sort((a, b) => (Number(a?.position ?? 0) - Number(b?.position ?? 0)) || String(a?.title || "").localeCompare(String(b?.title || "")))
                    .map((item, idx) => (
                      <div key={item?.id ?? idx} className="rounded-2xl border border-[#E6EAF0] bg-white p-5">
                        <div className="flex items-start gap-10">
                            {resolveImageUrl(item?.image_url) ? (
                              <img
                                src={resolveImageUrl(item?.image_url)}
                                alt={item?.title || "Feature icon"}
                                className="h-14 w-14 object-contain"
                                loading="lazy"
                              />
                            ) : (
                              featureIcon(item?.title)
                            )}
                          
                          <div>
                            <h2 className="text-[18px] font-semibold text-[#24303A]">{item?.title || ""}</h2>
                            <p className="mt-1 whitespace-pre-line text-[16px] w-full leading-relaxed text-[#6E7B86]">
                              {renderText(item?.description)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </section>

            {/* What we do */}
            <section className="rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm md:p-10">
              <h2 className="text-[20px] font-semibold text-[#24303A]">What we do</h2>
              <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {(whatWeDoData.length ? whatWeDoData : []).map((item, idx) => (
                  <div
                    key={item?.id ?? idx}
                    className="rounded-2xl border border-[#E6EAF0] bg-white p-5 text-center shadow-md"
                  >
                    <div className="mx-auto inline-flex items-center justify-center text-[#7352C7]">
                      {resolveImageUrl(item?.image_url) ? (
                        <img
                          src={resolveImageUrl(item?.image_url)}
                          alt={item?.title || "What we do icon"}
                          className="h-14 w-14 object-contain"
                          loading="lazy"
                        />
                      ) : (
                        whatWeDoIcon(item?.title)
                      )}
                    </div>
                    <h3 className="mt-3 text-[18px] font-semibold text-[#24303A]">
                      {item?.title || ""}
                    </h3>
                    <p className="mt-1 whitespace-pre-line text-[16px] leading-relaxed text-[#6E7B86]">
                      {renderText(item?.description)}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Why trust FEMTYZ */}
            <section className="rounded-3xl border border-[#E6EAF0] bg-white p-6 shadow-sm md:p-10">
              <h2 className="text-[20px] font-semibold text-[#24303A]">Why trust FEMTYZ?</h2>
              <div className="mt-4 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
                {(stepsData.length ? stepsData : []).map((item, idx) => (
                  <div
                    key={item?.id ?? idx}
                    className="flex flex-col items-center gap-5 rounded-2xl border border-[#E6EAF0] bg-white p-4"
                  >
                    <div className="inline-flex h-14 w-14 items-center justify-center text-[#7352C7]">
                      {resolveImageUrl(item?.image_url) ? (
                        <img
                          src={resolveImageUrl(item?.image_url)}
                          alt={item?.title || "Trust icon"}
                          className="h-10 w-10 object-contain"
                          loading="lazy"
                        />
                      ) : (
                        stepIcon(item?.title)
                      )}
                    </div>
                    <div>
                      <p className="text-[16px] font-medium text-[#24303A]">{item?.title || ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {privacyPromise?.title || privacyPromise?.description ? (
              <section className="relative overflow-hidden rounded-3xl border border-[#E6EAF0] bg-[#F6F3FF] p-6 shadow-sm md:p-10">
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center text-[#7352C7]">
                    {resolveImageUrl(privacyPromise?.image_url) ? (
                      <img
                        src={resolveImageUrl(privacyPromise?.image_url)}
                        alt={privacyPromise?.title || "Our Promise"}
                        className="h-10 w-10 object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                          d="M12.1 20.3 4.6 13a5 5 0 0 1 0-7.1 5 5 0 0 1 7.1 0l.3.3.3-.3a5 5 0 0 1 7.1 0 5 5 0 0 1 0 7.1l-7.5 7.3a1 1 0 0 1-1.4 0Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-[20px] font-semibold text-[#7352C7]">{privacyPromise?.title || ""}</h2>
                    <p className="mt-2 whitespace-pre-line text-[16px] leading-relaxed text-[#6E7B86]">
                      {renderText(privacyPromise?.description)}
                    </p>
                  </div>
                </div>

                <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 text-[#7352C7] opacity-90">
                  <svg className="h-7 w-14" viewBox="0 0 64 24" fill="none" aria-hidden="true">
                    <path
                      d="M18 6c-6 0-10 4-10 6s4 6 10 6c10 0 18-12 28-12 6 0 10 4 10 6s-4 6-10 6c-10 0-18-12-28-12Z"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </section>
            ) : null}

          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default About;
